import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { compactParams, formatTat, LIVE_REFRESH_MS, saveDownload } from "@/lib/reporting";
import { Page, PageHeader, StatusBadge, inputCls } from "@/components/lims";
import SampleStatusChart from "@/components/SampleStatusChart";
import SampleQrButton from "@/components/SampleQr";
import {
  ArrowSquareOut, DownloadSimple, Flask, Gauge, TestTube, Timer,
} from "@phosphor-icons/react";

const emptyFilters = { status: "", client_id: "", date_from: "", date_to: "" };

export default function Dashboard() {
  const [filters, setFilters] = useState(emptyFilters);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const { data } = await api.get("/dashboard/stats", {
        params: compactParams(filters),
      });
      setStats(data);
      setError("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load(true), LIVE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const set = (key) => (event) => setFilters({ ...filters, [key]: event.target.value });

  const exportRows = async (format) => {
    try {
      const response = await api.get("/dashboard/export", {
        params: { ...compactParams(filters), format },
        responseType: "blob",
      });
      saveDownload(response.data, `sample-status.${format}`,
        format === "pdf" ? "application/pdf" : "text/csv");
      toast.success(`${format.toUpperCase()} export downloaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const tat = stats?.tat || { overall: {}, by_client: [], over_target_samples: [] };
  const sampleClients = stats?.sample_client || [];
  const cards = [
    { label: "Filtered samples", value: stats?.total_samples ?? "—", icon: TestTube },
    { label: "Average TAT", value: formatTat(tat.overall?.average_tat_days), icon: Timer },
    { label: "Over target", value: tat.overall?.over_target_count ?? "—", icon: Gauge },
    { label: "Pending tests", value: stats?.pending_tests ?? "—", icon: Flask },
  ];

  return (
    <Page>
      <PageHeader title="Operations Dashboard"
        subtitle="Tenant-scoped counts and receipt-to-QA-release turnaround time"
        action={(
          <div className="flex gap-2">
            <button onClick={() => exportRows("csv")} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-white text-sm">
              <DownloadSimple size={17} /> CSV
            </button>
            <button onClick={() => exportRows("pdf")} className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-900 text-white text-sm">
              <DownloadSimple size={17} /> PDF
            </button>
          </div>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5" data-testid="dashboard-filters">
        <select className={inputCls} value={filters.status} onChange={set("status")} aria-label="Status filter">
          <option value="">All statuses</option>
          {(stats?.sample_status || []).map((row) => <option key={row.name}>{row.name}</option>)}
        </select>
        <select className={inputCls} value={filters.client_id} onChange={set("client_id")} aria-label="Client filter">
          <option value="">All clients</option>
          {clients.map((client) => <option key={client.id} value={client.id}>{client.legal_name}</option>)}
        </select>
        <input type="date" className={inputCls} value={filters.date_from} onChange={set("date_from")} aria-label="Received from" />
        <input type="date" className={inputCls} value={filters.date_to} onChange={set("date_to")} aria-label="Received to" />
      </div>

      {error && <div className="mb-4 rounded-md border border-status-critical/30 bg-status-critical/10 p-3 text-sm text-status-critical">{error}</div>}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        {cards.map(({ label, value, icon: Icon }) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex justify-between text-xs uppercase tracking-wider text-slate-500"><span>{label}</span><Icon size={20} /></div>
            <div className="mt-3 text-3xl font-display font-semibold text-slate-900">{loading && !stats ? "…" : value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="font-display font-medium text-slate-900">Samples by status</h2>
          <p className="text-xs text-slate-400 mt-1">Counts use the same filters as the list and exports.</p>
          <SampleStatusChart rows={stats?.sample_status || []} />
        </div>
        <div
          className="rounded-lg border border-slate-200 bg-white p-5 overflow-auto"
          data-testid="dashboard-client-counts"
        >
          <h2 className="font-display font-medium text-slate-900">Samples by client</h2>
          <p className="text-xs text-slate-400 mt-1 mb-3">Total matching samples for each client.</p>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-400">
              <tr><th className="text-left py-2">Client</th><th className="text-right">Samples</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sampleClients.length > 0 ? sampleClients.map((row) => (
                <tr key={row.client_id || row.name} data-client-id={row.client_id || "unassigned"}>
                  <td className="py-2">{row.name}</td>
                  <td className="text-right font-mono tabular-nums">{row.value}</td>
                </tr>
              )) : (
                <tr><td className="py-3 text-slate-400" colSpan={2}>No matching samples</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 overflow-auto">
          <h2 className="font-display font-medium text-slate-900">TAT by client</h2>
          <p className="text-xs text-slate-400 mt-1 mb-3">{tat.definition}</p>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-400"><tr><th className="text-left py-2">Client</th><th className="text-right">Released</th><th className="text-right">Average</th><th className="text-right">Over</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {tat.by_client?.map((row) => <tr key={row.client_id || row.client_name}><td className="py-2">{row.client_name}</td><td className="text-right">{row.released_count}</td><td className="text-right">{formatTat(row.average_tat_days)}</td><td className="text-right">{row.over_target_count}</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-auto">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div><h2 className="font-display font-medium text-slate-900">Matching samples</h2><p className="text-xs text-slate-400">{stats?.total_samples || 0} records; automatically refreshes every 15 seconds.</p></div>
          <span className="text-xs text-slate-400">
            Updated{" "}
            {stats?.generated_at ? (
              <time dateTime={stats.generated_at} data-timestamp>
                {new Date(stats.generated_at).toLocaleTimeString()}
              </time>
            ) : "—"}
          </span>
        </div>
        <table className="w-full text-sm" data-testid="dashboard-sample-list">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left px-5 py-3">Sample</th><th className="text-left px-5 py-3">Client</th><th className="text-left px-5 py-3">Received</th><th className="text-left px-5 py-3">Status</th><th /></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(stats?.matching_samples || []).map((sample) => (
              <tr key={sample.id}>
                <td className="px-5 py-3 font-mono text-xs">
                  <div className="flex items-center gap-1">
                    {sample.sample_code}
                    <SampleQrButton sampleId={sample.id} sampleCode={sample.sample_code} />
                  </div>
                </td>
                <td className="px-5 py-3">{sample.client_name}</td>
                <td className="px-5 py-3 text-slate-500">
                  {sample.received_at ? (
                    <time dateTime={sample.received_at} data-timestamp>
                      {sample.received_at.slice(0, 10)}
                    </time>
                  ) : "—"}
                </td>
                <td className="px-5 py-3"><StatusBadge status={sample.status} /></td>
                <td className="px-5 py-3 text-right"><Link to={`/samples/${sample.id}`} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900">Details <ArrowSquareOut size={14} /></Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {tat.over_target_samples?.length > 0 && (
        <div className="mt-4 rounded-lg border border-status-warn/30 bg-status-warn/10 p-5">
          <h2 className="font-display font-medium text-status-warn">Over-target drill-down</h2>
          <div className="mt-3 grid gap-2">
            {tat.over_target_samples.map((row) => <Link key={row.sample_id} to={`/samples/${row.sample_id}`} className="flex justify-between rounded bg-white px-3 py-2 text-sm"><span>{row.sample_code} · {row.client_name}</span><span>{formatTat(row.tat_days)} / target {formatTat(row.target_days)}</span></Link>)}
          </div>
        </div>
      )}
    </Page>
  );
}
